import React from 'react';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header';
import { useShop } from '../../contexts/ShopContext';

interface DashboardProps {
  user: any;
  onLogout: () => void;
}

interface Feature {
  id: string;
  icon: string;
  title: string;
  description: string;
  roles: string[];
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { name: shopName } = useShop();
  const allFeatures: Feature[] = [
    {
      id: 'employee-management',
      icon: '👥',
      title: 'Quản lý nhân viên',
      description: 'Quản lý thông tin nhân viên và ca làm việc',
      roles: ['admin']
    },
    {
      id: 'shift-management',
      icon: '⏰',
      title: 'Quản lý ca làm',
      description: 'Bắt đầu, chốt ca và xem lịch sử ca làm',
      roles: ['admin', 'employee']
    },
    {
      id: 'menu-management',
      icon: '📋',
      title: 'Quản lý menu',
      description: 'Thêm, sửa, xóa các món trong menu',
      roles: ['admin']
    },
    {
      id: 'create-order',
      icon: '🛒',
      title: 'Tạo đơn hàng',
      description: 'Tạo đơn hàng mới cho khách',
      roles: ['admin', 'employee']
    },
    {
      id: 'calculate-order',
      icon: '💰',
      title: 'Thanh toán đơn hàng',
      description: 'Thanh toán đơn hàng và thối tiền cho khách',
      roles: ['employee']
    },
    {
      id: 'all-orders',
      icon: '📋',
      title: 'Xem tất cả đơn hàng',
      description: 'Xem và quản lý tất cả đơn hàng trong hệ thống',
      roles: ['admin']
    },
    {
      id: 'reports',
      icon: '📊',
      title: 'Báo cáo',
      description: 'Xem báo cáo doanh thu và tiến độ',
      roles: ['admin']
    },
    {
      id: 'view-menu',
      icon: '📋',
      title: 'Xem menu',
      description: 'Xem danh sách món trong menu',
      roles: ['employee', 'customer']
    },
    {
      id: 'salary-management',
      icon: '💵',
      title: 'Quản lý lương',
      description: 'Tính lương và quản lý thanh toán lương nhân viên',
      roles: ['admin']
    },
    {
      id: 'my-salary',
      icon: '💳',
      title: 'Lương của tôi',
      description: 'Xem thông tin lương và lịch sử thanh toán',
      roles: ['employee']
    },
    {
      id: 'table-management',
      icon: '🏷️',
      title: 'Quản lý đơn đặt bàn',
      description: 'Quản lý sơ đồ bàn và trạng thái bàn',
      roles: ['employee', 'admin']
    },
    {
      id: 'table-admin-management',
      icon: '🪑',
      title: 'Quản lý bàn',
      description: 'Thêm, xóa, chỉnh sửa bàn',
      roles: ['admin']
    },
    {
      id: 'book-table',
      icon: '🪑',
      title: 'Đặt bàn',
      description: 'Xem sơ đồ bàn và đặt chỗ trước',
      roles: ['customer']
    },
    {
      id: 'my-reservations',
      icon: '🧾',
      title: 'Đơn đặt bàn của tôi',
      description: 'Xem đơn đặt bàn của tôi',
      roles: ['customer']
    },
    {
      id: 'material-management',
      icon: '📦',
      title: 'Quản lý kho',
      description: 'Nguyên liệu, tồn kho theo shop, nhập kho, cảnh báo sắp hết',
      roles: ['admin']
    },
    {
      id: 'shop-settings',
      icon: '⚙️',
      title: 'Cấu hình cửa hàng',
      description: 'Thuế, tên, logo của cửa hàng',
      roles: ['admin']
    },
    {
      id: 'account-settings',
      icon: '🧑',
      title: 'Tài khoản của tôi',
      description: 'Đổi tên, ảnh đại diện, xoá tài khoản',
      roles: ['admin', 'employee', 'customer']
    }
  ];

  const userFeatures = allFeatures.filter(feature => feature.roles.includes(user.role));

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin': return 'Quản lý';
      case 'employee': return 'Nhân viên';
      case 'customer': return 'Khách hàng';
      default: return role;
    }
  };

  const getWelcomeMessage = (role: string) => {
    switch (role) {
      case 'admin': return 'Chào mừng Quản lý! Bạn có quyền truy cập tất cả tính năng của hệ thống.';
      case 'employee': return 'Chào mừng Nhân viên! Hãy bắt đầu ca làm việc của bạn.';
      default: return `Chào mừng bạn đến với ${shopName || 'hệ thống'}!`;
    }
  };

  return (
    <div className="dashboard-container">
      <Header user={user} onLogout={onLogout}>
        <span className="user-role">{getRoleDisplayName(user.role)}</span>
      </Header>
      <main className="dashboard-main">
        <div className="welcome-section">
          <h2>
            <span className="emoji">🎉</span>
            <span> Chào mừng trở lại {shopName || 'POS'} </span>
            <span className="emoji">🎉</span>
          </h2>
          <div className="role-description">
            {getWelcomeMessage(user.role)}
          </div>
        </div>
        <div className="features-grid">
          {userFeatures.map((feature) => (
            <div
              key={feature.id}
              className="feature-card"
              onClick={
                feature.id === 'menu-management' || feature.id === 'view-menu' || feature.id === 'employee-management' || feature.id === 'shift-management' || feature.id === 'create-order' || feature.id === 'all-orders' || feature.id === 'calculate-order' || feature.id === 'reports' || feature.id === 'salary-management' || feature.id === 'my-salary' || feature.id === 'book-table' || feature.id === 'table-management' || feature.id === 'table-admin-management' || feature.id === 'my-reservations' || feature.id === 'material-management' || feature.id === 'shop-settings' || feature.id === 'account-settings'
                  ? () => navigate(
                      feature.id === 'employee-management' ? '/employees' :
                      feature.id === 'shift-management' ? '/shifts' :
                      feature.id === 'create-order' ? '/orders/new' :
                      feature.id === 'all-orders' ? '/orders' :
                      feature.id === 'calculate-order' ? '/orders/payment' :
                      feature.id === 'reports' ? '/reports' :
                      feature.id === 'salary-management' ? '/salaries' :
                      feature.id === 'my-salary' ? '/my-salary' :
                      feature.id === 'book-table' ? '/tables' :
                      feature.id === 'table-management' ? '/tables' :
                      feature.id === 'table-admin-management' ? '/tables/manage' :
                      feature.id === 'my-reservations' ? '/my-reservations' :
                      feature.id === 'material-management' ? '/materials' :
                      feature.id === 'shop-settings' ? '/settings' :
                      feature.id === 'account-settings' ? '/account' :
                      '/menu')
                  : undefined
              }
              style={
                feature.id === 'menu-management' || feature.id === 'view-menu' || feature.id === 'employee-management' || feature.id === 'shift-management' || feature.id === 'create-order' || feature.id === 'all-orders' || feature.id === 'calculate-order' || feature.id === 'reports' || feature.id === 'salary-management' || feature.id === 'my-salary' || feature.id === 'book-table' || feature.id === 'table-management' || feature.id === 'table-admin-management' || feature.id === 'my-reservations' || feature.id === 'material-management' || feature.id === 'shop-settings' || feature.id === 'account-settings'
                  ? { cursor: 'pointer' }
                  : {}
              }
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Dashboard; 